import React from 'react'

import { useAnnouncementBannerForm } from '../hooks/useAnnouncementBannerForm'
import { useProductMessageForSettings } from '../hooks/useProductMessageForSettings'

import { Box, Spinner, Text } from '@chakra-ui/react'

import {
  Button,
  Card,
  ConfirmRemovalDialog,
  DatePicker,
  FormTextareaControl,
  TimePicker,
} from '@/components/ui'
import { MAX_ANNOUNCEMENT_MESSAGE_LENGTH } from '@/utils/forms/validation'

// Extra styles added to make the textarea look the same as date selection inputs when disabled
const disabledTextareaStyles = {
  '&:disabled': {
    color: 'black',
    opacity: 0.5,
    backgroundColor: 'gray.100',
    borderColor: 'blue.600',
  },
} as const

/**
 * AnnouncementBannerSettings component for managing product announcement messages.
 */
export const AnnouncementBannerSettings = () => {
  const {
    currentMessage,
    isLoading: isLoadingMessages,
    error: loadError,
  } = useProductMessageForSettings()

  const { form, dates, handlers, state } =
    useAnnouncementBannerForm(currentMessage)

  if (loadError) {
    return (
      <Card p='4'>
        <Box color='gray.500' fontWeight='bold'>
          Error loading announcement settings
        </Box>
      </Card>
    )
  }

  if (isLoadingMessages) {
    return (
      <Card p='4'>
        <Box display='flex' justifyContent='center'>
          <Spinner color='gray.500' size='md' />
        </Box>
      </Card>
    )
  }

  return (
    <Card
      _hover={{
        boxShadow: '0px 2px 5px 6px rgba(0, 0, 0, 0.15)',
      }}
      p='0'
      transition='ease-in-out'
      transitionDuration='300ms'
      transitionProperty='all'
    >
      <Box p='4'>
        <Box fontSize='20px' fontWeight='extrabold' letterSpacing='-0.6px'>
          Announcement Banner
        </Box>
      </Box>

      <form onSubmit={form.handleSubmit(handlers.onCreateMessage)}>
        <Box
          bgColor='#F0F5FD'
          borderBottom='1px solid #eaedf3'
          borderTop='1px solid #eaedf3'
          p='4'
        >
          <Box display='flex' flexDirection='column' gap='4'>
            <FormTextareaControl
              errorMessage={form.errors.message && form.errors.message.message}
              id='message'
              isDisabled={state.isViewMode}
              isInvalid={!!form.errors?.message}
              label='Message'
              maxLength={MAX_ANNOUNCEMENT_MESSAGE_LENGTH}
              placeholder='Enter your update or downtime notice'
              sx={disabledTextareaStyles}
              textareaRef={form.register({
                required: 'Message is required',
                maxLength: {
                  value: MAX_ANNOUNCEMENT_MESSAGE_LENGTH,
                  message: `Message must be ${MAX_ANNOUNCEMENT_MESSAGE_LENGTH} characters or less`,
                },
                validate: (value) =>
                  value.trim().length > 0 || 'Message cannot be empty',
              })}
            />

            <Box>
              <Box display='flex' gap='4'>
                <Box flex='1'>
                  <Text
                    as='label'
                    color='gray.700'
                    display='block'
                    fontSize='14px'
                    fontWeight='bold'
                    htmlFor='startDate'
                    mb='2'
                    opacity={0.5}
                  >
                    Start Date
                  </Text>
                  <DatePicker
                    date={dates.startDate}
                    isDisabled={state.isViewMode}
                    minDate={new Date()}
                    name='startDate'
                    onChange={dates.setStartDate}
                  />
                </Box>

                <Box flex='1'>
                  <Text
                    as='label'
                    color='gray.700'
                    display='block'
                    fontSize='14px'
                    fontWeight='bold'
                    htmlFor='startTime'
                    mb='2'
                    opacity={0.5}
                  >
                    Start Time
                  </Text>
                  <TimePicker
                    isDisabled={state.isViewMode}
                    onChange={dates.setStartTime}
                    time={dates.startTime}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              <Box display='flex' gap='4'>
                <Box flex='1'>
                  <Text
                    as='label'
                    color='gray.700'
                    display='block'
                    fontSize='14px'
                    fontWeight='bold'
                    htmlFor='endDate'
                    mb='2'
                    opacity={0.5}
                  >
                    End Date
                  </Text>
                  <DatePicker
                    date={dates.endDate}
                    isDisabled={state.isViewMode}
                    minDate={dates.startDate}
                    name='endDate'
                    onChange={dates.setEndDate}
                  />
                </Box>

                <Box flex='1'>
                  <Text
                    as='label'
                    color='gray.700'
                    display='block'
                    fontSize='14px'
                    fontWeight='bold'
                    htmlFor='endTime'
                    mb='2'
                    opacity={0.5}
                  >
                    End Time
                  </Text>
                  <TimePicker
                    isDisabled={state.isViewMode}
                    onChange={dates.setEndTime}
                    time={dates.endTime}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              as='p'
              color='gray.600'
              fontSize='sm'
              fontStyle='italic'
              mt='2'
            >
              All times displayed in Pacific Time (PST)
            </Box>
          </Box>
        </Box>

        <Box p='4'>
          {state.isViewMode ? (
            <Button onClick={handlers.handleOpenDeleteModal} variant='danger'>
              Delete
            </Button>
          ) : (
            <Button
              isDisabled={!state.isFormValid}
              isLoading={state.isCreating}
              type='submit'
            >
              Post Note
            </Button>
          )}
        </Box>
      </form>

      <ConfirmRemovalDialog
        actionButtonText='Delete'
        bodyText='Are you sure you want to delete this announcement banner '
        handleOnClick={handlers.onDeleteMessage}
        isLoading={state.isDeleting}
        isOpen={state.isDeleteModalOpen}
        itemTitle={
          currentMessage?.message?.substring(0, 50) + '...' ||
          'this announcement banner'
        }
        onClose={handlers.handleCloseDeleteModal}
        title='Delete Announcement Banner'
      />
    </Card>
  )
}
